import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import { USER_ACTIONS, isAllowedAction } from "../config/auth";

const ParticipantDetailsDialog = ({ open, onClose, participant, onEdit, mappings, schemaAsObject }) => {
    if (!participant) return null;
    // Function to render values based on their type
    // console.log(participant, mappings, schemaAsObject);

    const renderValue = (value) => {
        if (Array.isArray(value)) {
            return value.length > 0 ? value.map((item, index) => (
                <Typography key={index} variant="body2">{item.value} ({item.language})</Typography>
            )) : <Typography variant="body2">N/A</Typography>;
        }
        return <Typography variant="body2">{value || 'N/A'}</Typography>;
    };

    const renderLabel = (value, vocab) => {
        if (Array.isArray(value)) {
            return value.length > 0 ? value.map((item, index) => {
                // Find the matching vocab object by value
                const vocabItem = vocab.find(v => v.value === item.value);
                return (
                    <Typography key={index} variant="body2">
                        {vocabItem ? vocabItem.label : 'N/A'} ({item.language})
                    </Typography>
                );
            }) : <Typography variant="body2">N/A</Typography>;
        }
        // Handle single values (not arrays)
        const vocabItem = vocab.find(v => v.value === value);
        return <Typography variant="body2">{vocabItem ? vocabItem.label : 'N/A'}</Typography>;
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth={"lg"}>
            <DialogTitle>{`Details for ${participant.name[0]?.value || 'N/A'}`}</DialogTitle>
            <DialogContent>
                <Grid container spacing={1} direction="row">
                    <Grid item container size={12}>
                        <Grid item size={6}>
                            <Typography variant="h6">ID</Typography>
                            {renderValue(participant.id)}
                        </Grid>
                        <Grid item size={6}>
                            <Typography variant="h6">Approval status</Typography>
                            {renderValue(participant.management.approval_status)}
                        </Grid>
                    </Grid>
                    <Grid item container size={12}>
                        <Grid item size={6}>
                            <Stack spacing={1}>
                                <Typography variant="h6">{schemaAsObject.properties.address.title}</Typography>
                                {participant.address?.street_address.length > 0 ? (
                                    <>
                                        {renderValue(participant.address.street_address)}
                                        {renderValue(participant.address.administrative_area)}
                                        {renderValue(participant.address.city)}
                                        {renderValue(participant.address.postal_code)}
                                        {renderLabel(participant.address.country, mappings.Country.vocab)}
                                    </>) : (<Typography variant="body2">N/A</Typography>)
                                }
                                <Typography variant="h6">{schemaAsObject.properties.other_address.title}</Typography>
                                {participant.other_address?.street_address.length > 0 ? (
                                    <>
                                        {renderValue(participant.other_address.street_address)}
                                        {renderValue(participant.other_address.administrative_area)}
                                        {renderValue(participant.other_address.city)}
                                        {renderValue(participant.other_address.postal_code)}
                                        {renderLabel(participant.other_address.country, mappings.Country.vocab)}
                                    </>) : (<Typography variant="body2">N/A</Typography>)
                                }
                                <Typography variant="h6">{schemaAsObject.properties.legal_representative.title}</Typography>
                                {participant.legal_representative && (<>
                                    {renderValue(participant.legal_representative.name)}
                                    {participant.legal_representative.email.length > 0 ? (
                                        participant.legal_representative.email.map((item, index) => (
                                            <Typography key={index} variant="body2">
                                                {item}
                                            </Typography>
                                        ))
                                    ) : <Typography variant="body2">N/A</Typography>}
                                </>
                                )}

                                <Typography variant="h6">{schemaAsObject.properties.technical_contact.title}</Typography>
                                {participant?.technical_contact ? (
                                    <>
                                        {renderValue(participant.technical_contact.name)}
                                        {participant.technical_contact.email.length > 0 && (
                                            participant.technical_contact.email.map((item, index) => (
                                                <Typography key={index} variant="body2">
                                                    {item}
                                                </Typography>
                                            )))}
                                    </>) : (<Typography variant="body2">N/A</Typography>)
                                }
                            </Stack>
                        </Grid>
                        <Grid item size={6}>
                            <Stack spacing={1}>
                                <Typography variant="h6">{schemaAsObject.properties.short_name.title}</Typography>
                                {renderValue(participant.short_name)}
                                <Typography variant="h6">{schemaAsObject.properties.vat.title}</Typography>
                                {renderValue(participant.vat)}
                                <Typography variant="h6">{schemaAsObject.properties.eu_id.title}</Typography>
                                {renderValue(participant.eu_id)}
                                <Typography variant="h6">{schemaAsObject.properties.organization_type.title}</Typography>
                                {renderValue(participant.organization_type)}
                                <Typography variant="h6">{schemaAsObject.properties.company_type.title}</Typography>
                                {renderLabel(participant.company_type, mappings.CompanyType.vocab)}
                                <Typography variant="h6">{schemaAsObject.properties.location_of_incorporation.title}</Typography>
                                {renderLabel(participant.location_of_incorporation, mappings.Country.vocab)}
                                <Typography variant="h6">{schemaAsObject.properties.identifier.title}</Typography>
                                {participant.identifier.length > 0 ? (
                                    <>
                                        {participant.identifier.map((item, index) => (<>
                                            <Typography key={index} variant="body2">
                                                <a href={item.identifier_notation} target="_blank" rel="noopener noreferrer">{item.identifier_notation}</a>
                                            </Typography>
                                            {renderValue(item.creator)}
                                        </>
                                        ))}
                                    </>
                                ) : (<Typography variant="body2">N/A</Typography>)
                                }
                            </Stack>
                        </Grid>
                    </Grid>

                    <Grid item container size={12}>
                        {/*  <Grid item size={6}>
                            <Stack spacing={1}>
                                <Typography variant="h6">{schemaAsObject.properties.contact_website.title}</Typography>
                                {participant.contact_website.length > 0 ? (
                                    participant.contact_website.map((url, index) => (
                                        <Typography key={index} variant="body2">
                                            <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
                                        </Typography>
                                    ))
                                ) : <Typography variant="body2">N/A</Typography>}
                                {participant.logo ? <Typography variant="h6">{schemaAsObject.properties.logo.title}</Typography> : <Typography variant="h6">Logo</Typography>}
                                {renderValue(participant.logo)}
                                {participant.logo && (
                                    <Box component="img" src={participant.logo} alt="Logo" sx={{ width: "100px", height: "auto", mt: 1 }} />
                                )}
                            </Stack>
                                </Grid> */}

                        <Grid item size={6}>
                            <Stack spacing={1}>
                                <Typography variant="h6">{schemaAsObject.properties.activities.title}</Typography>
                                {participant.activities && participant.activities.length > 0 ? participant.activities.map((activity, index) => (
                                    <div key={index}>
                                        {activity.map((item, subIndex) => (
                                            <Typography key={subIndex} variant="body2">{item.value} ({item.language})</Typography>
                                        ))}
                                    </div>
                                )) : <Typography variant="body2">N/A</Typography>}
                                <Typography variant="h6">{schemaAsObject.properties.description.title}</Typography>
                                {renderValue(participant.description)}
                            </Stack>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                {isAllowedAction(USER_ACTIONS.ADMINISTER_PARTICIPANT_REGISTRY) && <Button onClick={() => onEdit(participant)}>Edit participant</Button>}
                {isAllowedAction(USER_ACTIONS.UPDATE_OWN_FORM) && <Button onClick={() => onEdit(participant)}>Edit my info</Button>}
            </DialogActions>
        </Dialog>
    );
};

export default ParticipantDetailsDialog;
